import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createCandidateNotes, getCandidateSummary, updateCandidateNotes } from '../API/candidateApi';
import { getQueryResults } from '../API/queryApi';
import type { RootState } from '../store/store';
import { mapAttributes, mapFlags, mapNotes } from '../transform/candidateTransform';

export interface IEntityNotes {
  [key: string]: {
    id?: string,
    entityId: string,
    name: string,
    displayName: string,
    description: string,
    content: string,
    taxonomyType: string,
    ownerId?: string
  }
}

export interface IEntityFlags {
  id: string,
  name: string,
  type: string,
  value: number,
  entityId: string,
}

export const initialNoteValue = {
  entityId: '',
  name: '',
  displayName: '',
  description: '',
  content: '',
  taxonomyType: '',
}

export interface ICandidate {
  id: string,
  entityType?: string[],
  nodeType?: string[];
  href?: string,
  schemaName?: string,
  schemaUrl?: string,
  taxonomyName?: string[],
  updatedBy?: string[],
  version?: string,
  url?: string,
  deleted?: boolean,
  changeDescription?: any,
  globalLocationNumber?: string,
  homeLocation?: string,
  workLocation?: string,
  address?: string,
  location?: string,
  name: string,
  updatedAt: any,
  notes: IEntityNotes
  flags: IEntityFlags[],
}

export const NOTE_SECTIONS = {
  risk: 'risk',
  info: 'info',
  summary: 'summary',
  references: 'references',
  graph: 'graph',
  query: 'query'
}

export const notesInitializationValue = { 
  [NOTE_SECTIONS.risk]: { ...initialNoteValue },
  [NOTE_SECTIONS.info]: { ...initialNoteValue },
  [NOTE_SECTIONS.summary]: { ...initialNoteValue },
  [NOTE_SECTIONS.references]: { ...initialNoteValue },
  [NOTE_SECTIONS.graph]: { ...initialNoteValue },
  [NOTE_SECTIONS.query]: { ...initialNoteValue },
}

export const initialCandidateValue: ICandidate = {
  id: '',
  entityType: [],
  nodeType: [''],
  name: [''],
  updatedAt: [],
  notes: { ...notesInitializationValue },
  flags: [],
}

export interface ICandidateSummary {
    criteria: string,
    totalFlags: number,
    directFlags: number,
    indirectFlags: number,
    relationshipFlags: number,
    riskCategory: string,
    preliminaryRecommendation: string
}

interface CandidateState {
    candidateDetails: ICandidate,
    candidateSummary: ICandidateSummary[],
    candidateList: any[]
}

export const initialCandidateSummary: ICandidateSummary[] = []

export const callGetSearchSummary = createAsyncThunk<any, {entityId: string, entityType: string}>(
  'candidate/getSummary',
  async ( payload, { dispatch }) => {
    const response = await getQueryResults(payload.entityId);
    const returnValue = (mapAttributes({...response?.data?.candidatesAttributes}) as unknown as ICandidate)
    returnValue.notes = mapNotes(response.data.notes || []);
    returnValue.flags = mapFlags(response.data.flags || []);
    dispatch(setCandidateDetails(returnValue));
    return response.data.search;
  }
)

export const callGetEntitySummary = createAsyncThunk<any, { entityId: string, entityType: string }>(
  'candidate/getEntitySummary',
  async (payload, { dispatch }) => {
    const response = await getCandidateSummary(payload.entityType, payload.entityId);
    const returnValue = response.data[payload.entityType];
    returnValue.notes = mapNotes(response.data.notes || []);
    returnValue.flags = mapFlags(response.data.flags || []);
    dispatch(setCandidateDetails(returnValue))
  }
)

export const callCreateNotes = createAsyncThunk(
  'candidate/create-notes',
  async (payload: any) => {
    await createCandidateNotes(payload);
  }
)

export const callUpdateNote = createAsyncThunk(
  'candidate/update-notes',
  async (payload: any) => {
    await updateCandidateNotes(payload);
  }
)

// Define the initial state using that type
const initialState: CandidateState = {
    candidateDetails: initialCandidateValue,
    candidateSummary: initialCandidateSummary,
    candidateList: []
};


export const candidateSlice = createSlice({
  name: 'candidate',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setCandidateDetails: (state, action: PayloadAction<ICandidate>) => {
        state.candidateDetails = action.payload;
    },
    setCandidateSummary: (state, action: PayloadAction<ICandidateSummary[]>) => {
      state.candidateSummary = action.payload;
    },
    setCandidateList: (state, action: PayloadAction<any[]>) => {
      state.candidateList = action.payload;
    }
  }
});

export const {
  setCandidateDetails,
  setCandidateSummary,
  setCandidateList
} = candidateSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectCandidateDetails = (state: RootState): ICandidate => state.candidate.candidateDetails;
export const selectCandidateSummary = (state: RootState): ICandidateSummary[] => state.candidate.candidateSummary;
export const selectCandidateList = (state: RootState): any[] => state.candidate.candidateList;

export default candidateSlice.reducer;
